import { useCallback, useEffect, useMemo } from "react";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";

import { type SectionItem, SideNavigationMenu } from "../../../Components/Menu/SideNavigationMenu/SideNavigationMenu";
import { type Dashboard } from "../../../Context/useDashboardsContext";
import { getDashboardNameById } from "../../../utils/common";
import mixpanel from "../../../utils/mixpanel";
import { OrganizationDashboardsDialog } from "./Dialogs/OrganizationDashboardsDialog";
import { type DashboardOperations, useDashboardTypes } from "./useDashboardTypes";
import { sortDashboardsByType } from "./utils";

type Props = {
  openOrganizationsDialog: boolean;
  selectedDashboardId: string | null;
  disableNewDashboardCreation: boolean;
  onNavigateToDashboard: (name: string) => void;
  onNewDashboardClicked: () => void;
  onUpdateCurrentDashboardOperations: (operations: DashboardOperations) => void;
  onOrganizationsDialogChanged: (open: boolean) => void;
};

export function DashboardSideMenu({
  openOrganizationsDialog,
  selectedDashboardId,
  disableNewDashboardCreation,
  onNavigateToDashboard,
  onNewDashboardClicked,
  onUpdateCurrentDashboardOperations,
  onOrganizationsDialogChanged,
}: Props) {
  const {
    preset,
    user,
    selectedOrganization,
    unselectedOrganization,
    currentDashboardOperations,
    updateOrganizations,
  } = useDashboardTypes();

  useEffect(() => {
    onUpdateCurrentDashboardOperations(currentDashboardOperations);
  }, [currentDashboardOperations, onUpdateCurrentDashboardOperations]);

  const itemsFromDashboards = useCallback(
    (dashboards: Dashboard[]) =>
      dashboards.map((dashboard) => ({
        name: getDashboardNameById(dashboard.id, dashboard.name),
        selected: dashboard.id === selectedDashboardId,
        onClick: () => {
          onNavigateToDashboard(dashboard.name);
        },
      })),
    [selectedDashboardId, onNavigateToDashboard]
  );

  const sortedPreset = useMemo(() => sortDashboardsByType(preset), [preset]);

  const presetSection = useMemo(() => {
    const section: SectionItem = {
      name: "Preset dashboards",
      emptyItemsLabel: "No dashboards available",
      items: itemsFromDashboards(sortedPreset),
    };
    return section;
  }, [itemsFromDashboards, sortedPreset]);

  const userSection = useMemo(
    () => ({
      name: "My dashboards",
      emptyItemsLabel: "No dashboards created",
      sideIcon: <AddIcon />,
      sideIconOnClick: () => {
        mixpanel.track("dashboards.create-new-dashboard");
        onNewDashboardClicked();
      },
      sideIconDisabled: disableNewDashboardCreation,
      items: itemsFromDashboards(user),
    }),
    [user, itemsFromDashboards, onNewDashboardClicked, disableNewDashboardCreation]
  );

  const organizationSection = useMemo(
    () => ({
      name: "Organization dashboards",
      emptyItemsLabel: "No dashboards added",
      sideIcon: <SearchIcon />,
      sideIconOnClick: () => {
        mixpanel.track("dashboards.browse-org-dashboards");
        onOrganizationsDialogChanged(true);
      },
      items: itemsFromDashboards(selectedOrganization),
    }),
    [itemsFromDashboards, selectedOrganization, onOrganizationsDialogChanged]
  );

  return (
    <>
      {openOrganizationsDialog && (
        <OrganizationDashboardsDialog
          open={openOrganizationsDialog}
          onClose={() => {
            onOrganizationsDialogChanged(false);
          }}
          selected={selectedOrganization}
          unselected={unselectedOrganization}
          onSelectionChange={(newSelected: Dashboard[]) => {
            const newlyAdded = newSelected.filter(
              (dashboard) => !selectedOrganization.some((selected) => selected.name === dashboard.name)
            );

            if (newlyAdded.length > 0) {
              onNavigateToDashboard(newlyAdded[0].name);
            } else if (newSelected.length === 0 && selectedOrganization.length > 0) {
              onNavigateToDashboard(selectedOrganization[0].name);
            }

            return updateOrganizations(newSelected);
          }}
        />
      )}
      <SideNavigationMenu sections={[presetSection, userSection, organizationSection]} />
    </>
  );
}
